import { useFormik } from 'formik'
import owasp, { TestResult } from 'owasp-password-strength-test'
import React from 'react'
import { AcceptInvite, Invite, User } from '@citruscamps/citrus-client'
import { LoadingButton } from '../../../../../components/LoadingButton/LoadingButton'
import * as Yup from 'yup'
import translations from '../../../../../translations/en.json'

interface IProps {
  code: string
  user?: User
  invite: Invite
  onSubmit: (value: AcceptInvite) => Promise<void>
}

export const AcceptInviteForm = ({ user, code, invite, ...props }: IProps) => {
  const {
    values,
    errors,
    touched,
    handleSubmit,
    handleChange,
    handleBlur,
    setFieldError,
    isSubmitting,
  } = useFormik<AcceptInvite>({
    initialValues: {
      code: code || '',
      //same for the following
      first_name: invite.first_name || '',
      last_name: invite.last_name || '',
      password: '',
      program_id: invite.program.id || '',
    },
    validationSchema: Yup.object().shape({
      first_name: Yup.string()
        .required((translations as any)['error.first_name_required'])
        .max(40, (translations as any)['error.first_name_maxlength']),
      last_name: Yup.string()
        .max(40, (translations as any)['error.last_name_maxlength'])
        .nullable(),
      password: Yup.string()
        .test('required', (translations as any)['error.password_required'], function (val) {
          return !!user || !!val
        })
        .test('password', '', function (val) {
          const passwordStrength: TestResult = owasp.test(val || '')
          return (
            !!user ||
            (val &&
            !passwordStrength?.strong &&
            passwordStrength?.errors &&
            passwordStrength?.errors?.length > 0
              ? this.createError({
                  message: passwordStrength.errors[0] || '',
                  path: 'password',
                })
              : true)
          )
        }),
    }),
    onSubmit: async (value, { setSubmitting }) => {
      try {
        await props.onSubmit(value)
      } catch (e: any) {
        if (e.param) {
          setFieldError(e.param, e.message)
        } else if (e.message) {
          setFieldError('email', e.message)
        }
      }
      setSubmitting(false)
    },
  })
  return (
    <form onSubmit={handleSubmit}>
      <div className="mb-3">
        <h4>Join the {invite?.program?.name || ''} Program</h4>
        {user ? (
          <p className="mt-4">
            Welcome back, {user.first_name}! You have been invited to join another Citrus
            organization.
          </p>
        ) : (
          <>
            <div className="mb-3 pb-2">
              <p>
                You have been invited to join {invite?.program?.name} on Citrus. Please fill out the
                following fields to finish creating your Citrus account.
              </p>
            </div>
            <div className="mb-3">
              <label>Your first name</label>
              <input
                id="first_name"
                name="first_name"
                autoComplete="given-name"
                type="text"
                className={
                  'form-control' + (touched.first_name && !!errors.first_name ? ' is-invalid' : '')
                }
                value={values.first_name}
                onChange={handleChange}
                onBlur={handleBlur}
                autoFocus
                required
              />
              <div className="invalid-feedback d-block">
                {touched?.first_name && !!errors?.first_name ? errors?.first_name : '\u00A0'}
              </div>
            </div>
            <div className="mb-3">
              <label className="optional">Your last name</label>
              <input
                id="last_name"
                name="last_name"
                autoComplete="family-name"
                type="text"
                className={
                  'form-control' + (touched.last_name && !!errors.last_name ? ' is-invalid' : '')
                }
                value={values.last_name}
                onChange={handleChange}
                onBlur={handleBlur}
              />
              <div className="invalid-feedback d-block">
                {touched?.last_name && !!errors?.last_name ? errors?.last_name : '\u00A0'}
              </div>
            </div>
            <div className="mb-3">
              <label>Your new password</label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="new-password"
                className={
                  'form-control' + (touched.password && !!errors.password ? ' is-invalid' : '')
                }
                value={values.password}
                onChange={handleChange}
                onBlur={handleBlur}
                required
              />
              <div className="invalid-feedback d-block">
                {touched?.password && !!errors?.password ? errors?.password : '\u00A0'}
              </div>
            </div>
          </>
        )}
        <div className="mb-3 mt-4 text-center">
          <LoadingButton
            type="submit"
            className="btn btn-primary btn-block d-md-inline w-md-auto"
            loading={isSubmitting}
          >
            Join Program
          </LoadingButton>
        </div>
      </div>
    </form>
  )
}
